import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Textarea, TypographyMuted, TypographySmall } from '@shared/ui';
import { getApiErrorMessage } from '@shared/lib/get-api-error-message';
import type { ComplianceMessage } from '@shared/api/contracts/compliance';
import { MESSAGE_MAX } from '@entities/compliance';
import { formatDateTime } from '@shared/lib/format-date';

interface ComplianceThreadProps {
  messages: ComplianceMessage[];
  canReply: boolean;
  reply: (text: string) => Promise<void>;
}

export function ComplianceThread({ messages, canReply, reply }: ComplianceThreadProps) {
  const { t } = useTranslation('cabinet');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed || trimmed.length > MESSAGE_MAX) {
      setError(t('compliance.thread.invalid'));
      return;
    }
    setSending(true);
    setError(null);
    try {
      await reply(trimmed);
      setText('');
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, t, 'compliance.errors.generic'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-3">
      {messages.length === 0 ? <TypographyMuted>{t('compliance.thread.empty')}</TypographyMuted> : null}
      <ul className="space-y-2">
        {messages.map((message) => (
          <li key={message.id} className="rounded-xl border border-border bg-background px-3 py-2">
            <TypographySmall>
              {t(`compliance.thread.role.${message.authorRole}`, { defaultValue: message.authorRole })}
            </TypographySmall>
            <p className="mt-1 whitespace-pre-wrap break-words text-sm text-foreground">{message.text}</p>
            <p className="mt-1 text-xs text-muted-foreground">{formatDateTime(message.createdAt)}</p>
          </li>
        ))}
      </ul>
      {canReply ? (
        <div className="space-y-2">
          <Textarea
            value={text}
            maxLength={MESSAGE_MAX}
            rows={3}
            placeholder={t('compliance.thread.placeholder')}
            onChange={(event) => setText(event.target.value)}
          />
          {error ? <p className="text-xs text-destructive">{error}</p> : null}
          <TypographyMuted>{t('compliance.thread.sentHint')}</TypographyMuted>
          <Button type="button" className="w-full sm:w-auto" loading={sending} onClick={() => void submit()}>
            {t('compliance.thread.send')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
